import { type ApolloClient, useApolloClient } from '@apollo/client';
import type { Session } from '@openx/components-core/lib/AiChat/components/SessionList';
import type { Message } from '@openx/components-core/lib/AiChat/types';
import type {
  UseDealTroubleshootingAgentMessageMutationState,
  UseDealTroubleshootingAgentSendMessage,
  UseDealTroubleshootingAgentSessionHistory,
  UseDealTroubleshootingAgentSessionList,
} from '@openx/components-deal/lib/utils/context/DealHookTypes';
import { QueryKeys } from '@openx/utils/api/queryKeys';
import { queryOptions, useMutation, useMutationState, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DealTroubleshootingAgentAsyncQueryDocument,
  type DealTroubleshootingAgentAsyncQueryMutation,
} from '../../../../graphql/mutation/deals/DealTroubleshootingAgentAsyncQueryMutation.generated';
import {
  GetDealTroubleshootingAgentSessionHistoryDocument,
  type GetDealTroubleshootingAgentSessionHistoryQuery,
} from '../../../../graphql/query/deals/GetDealTroubleshootingAgentSessionHistory.generated';
import {
  GetDealTroubleshootingAgentSessionListDocument,
  type GetDealTroubleshootingAgentSessionListQuery,
} from '../../../../graphql/query/deals/GetDealTroubleshootingAgentSessionList.generated';

export const useDealTroubleshootingAgentSessionList: UseDealTroubleshootingAgentSessionList = dealId => {
  const apolloClient = useApolloClient();

  return useQuery({
    queryKey: [QueryKeys.DEAL_TROUBLESHOOTING_AGENT, 'sessionList', dealId],
    queryFn: async () => {
      const response = await apolloClient.query<GetDealTroubleshootingAgentSessionListQuery>({
        query: GetDealTroubleshootingAgentSessionListDocument,
        variables: { deal_id: dealId },
        fetchPolicy: 'network-only',
      });

      const sessions: Session[] = response.data.dealTroubleshootingAgentSessionList?.sessions ?? [];

      return { sessions };
    },
  });
};

const sessionHistoryQueryOptions = (dealId: string, sessionId: string, apolloClient: ApolloClient) =>
  queryOptions({
    queryKey: [QueryKeys.DEAL_TROUBLESHOOTING_AGENT, 'sessionHistory', dealId, sessionId],
    queryFn: async () => {
      if (!sessionId) {
        throw new Error('Session id is required');
      }

      const response = await apolloClient.query<GetDealTroubleshootingAgentSessionHistoryQuery>({
        query: GetDealTroubleshootingAgentSessionHistoryDocument,
        variables: {
          deal_id: dealId,
          session_id: sessionId,
          include_agent_steps: true,
        },
        fetchPolicy: 'network-only',
      });

      return { messages: (response.data.dealTroubleshootingAgentSessionHistory?.messages ?? []) as Message[] };
    },
    staleTime: 5000, // prevent double refetch in quick succession
    refetchIntervalInBackground: true, // keep polling while the tab is hidden
    meta: { disableNotification: true },
  });

export const useDealTroubleshootingAgentSessionHistory: UseDealTroubleshootingAgentSessionHistory = ({
  dealId,
  sessionId,
  enabled = true,
  refetchInterval,
}) => {
  const apolloClient = useApolloClient();

  return useQuery({
    ...sessionHistoryQueryOptions(dealId, sessionId ?? '', apolloClient),
    enabled: enabled && !!sessionId,
    refetchInterval,
  });
};

export const useDealTroubleshootingAgentSendMessage: UseDealTroubleshootingAgentSendMessage = (dealId, sessionId) => {
  const apolloClient = useApolloClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [QueryKeys.DEAL_TROUBLESHOOTING_AGENT, 'sendMessage', dealId, sessionId],
    mutationFn: async ({ message, sessionId }: { message: string; sessionId?: string; prefetchMessages?: boolean }) => {
      const response = await apolloClient.mutate<DealTroubleshootingAgentAsyncQueryMutation>({
        context: {
          disableGlobalNotification: true,
        },
        mutation: DealTroubleshootingAgentAsyncQueryDocument,
        variables: {
          deal_id: dealId,
          user_message: message,
          session_id: sessionId,
        },
      });

      const payload = response.data?.dealTroubleshootingAgentAsyncQuery;
      const nextSessionId = payload?.session_id;
      const responseDealId = payload?.deal_id;

      if (!nextSessionId || !responseDealId) {
        throw new Error('Failed to send troubleshooting agent message');
      }

      return {
        session_id: nextSessionId,
        deal_id: responseDealId,
      };
    },
    onSuccess: async (response, { prefetchMessages }) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.DEAL_TROUBLESHOOTING_AGENT],
        }),
        prefetchMessages && response?.session_id
          ? queryClient.prefetchQuery(sessionHistoryQueryOptions(response.deal_id, response.session_id, apolloClient))
          : Promise.resolve(),
      ]);
      // Reset temporary chat cache after invalidating history to prevent removing and re-adding user message from UI
      return queryClient.resetQueries({
        predicate(query) {
          return query.queryKey[0] === QueryKeys.DEAL_TROUBLESHOOTING_AGENT && query.queryKey[3] === 'temporary-chat';
        },
      });
    },
  });
};

export const useDealTroubleshootingAgentMessageMutationState: UseDealTroubleshootingAgentMessageMutationState = (
  dealId,
  sessionId
) => {
  return useMutationState({
    filters: {
      mutationKey: [QueryKeys.DEAL_TROUBLESHOOTING_AGENT, 'sendMessage', dealId, sessionId],
      status: 'pending',
    },
  });
};
